// Inspection Cache Utilities for PWA
// Provides offline caching of in-progress inspection drafts using IndexedDB

import { getUserId } from './tokenUtils';

const DB_NAME = 'RNR_INSPECTION_CACHE';
const DB_VERSION = 2;
const STORE_NAME = 'inspections';

const getCurrentOwnerUserId = () => {
  try {
    const id = getUserId();
    return id ? String(id) : '';
  } catch {
    return '';
  }
};

const isOwnerMatch = (draft, ownerUserId) => {
  // If we can't identify a user, do not block access (offline/no auth context).
  if (!ownerUserId) return true;

  const draftOwner = draft?.ownerUserId ? String(draft.ownerUserId) : '';

  // Legacy drafts without an owner are treated as NOT visible to avoid cross-user leakage.
  if (!draftOwner) return false;

  return draftOwner === ownerUserId;
};

class InspectionCacheManager {
  constructor() {
    this.db = null;
    this.dbPromise = this.initDB();
  }

  normalizeResponseId(val) {
    if (val === undefined || val === null) return '';
    const s = String(val).trim();
    return s;
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
          store.createIndex('templateId', 'templateId', { unique: false });
          store.createIndex('companyId', 'companyId', { unique: false });
          store.createIndex('updatedOn', 'updatedOn', { unique: false });
          store.createIndex('ownerUserId', 'ownerUserId', { unique: false });
        } else {
          const store = request.transaction.objectStore(STORE_NAME);
          if (!store.indexNames.contains('ownerUserId')) {
            store.createIndex('ownerUserId', 'ownerUserId', { unique: false });
          }
        }
      };
    });
  }

  // Generate a human-readable draft ID like INS-ABC123...
  generateDraftId() {
    const now = Date.now();
    const random = Math.floor(Math.random() * 0xffff);
    const combined = (now << 4) ^ random;
    const hex = combined.toString(16).toUpperCase();
    return `INS-${hex}`;
  }

  async saveDraft(draft) {
    try {
      await this.dbPromise;
      const now = Date.now();
      const draftId = draft.draftId || this.generateDraftId();
      const cacheKey = draft.cacheKey || draftId;

      const ownerUserId = String(draft.ownerUserId || getCurrentOwnerUserId() || '');
      const responseId = this.normalizeResponseId(draft.responseId || draft?.data?.responseId);
      const data = {
        ...draft,
        draftId,
        cacheKey,
        ownerUserId,
        responseId: responseId || draft.responseId || null,
        updatedOn: now,
        createdOn: draft.createdOn || now,
      };

      return new Promise((resolve, reject) => {
        const tx = this.db.transaction([STORE_NAME], 'readwrite');
        const store = tx.objectStore(STORE_NAME);

        // Ensure there is only ONE local draft per server-linked inspection (responseId).
        // This prevents duplicated records in IndexedDB when cache keys change (e.g. INS-* -> resp-*).
        if (responseId) {
          const getAllReq = store.getAll();
          getAllReq.onerror = () => reject(getAllReq.error);
          getAllReq.onsuccess = () => {
            try {
              const all = Array.isArray(getAllReq.result) ? getAllReq.result : [];
              for (const d of all) {
                if (!d) continue;
                const dOwner = d?.ownerUserId ? String(d.ownerUserId) : '';
                if (ownerUserId && dOwner && dOwner !== ownerUserId) continue;
                const dResp = this.normalizeResponseId(d.responseId || d?.data?.responseId);
                if (!dResp || dResp !== responseId) continue;
                const dKey = d?.cacheKey ? String(d.cacheKey) : '';
                if (!dKey || dKey === String(cacheKey)) continue;
                try {
                  store.delete(dKey);
                } catch {}
              }
            } catch {}

            const request = store.put(data);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(true);
          };
          return;
        }
        const request = store.put(data);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(true);
      });
    } catch (error) {
      console.error('Error saving inspection draft to cache:', error);
      return false;
    }
  }

  async getDraft(cacheKey) {
    try {
      await this.dbPromise;
      const ownerUserId = getCurrentOwnerUserId();
      return new Promise((resolve, reject) => {
        const tx = this.db.transaction([STORE_NAME], 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(cacheKey);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result || null;
          if (!result) return resolve(null);
          if (!isOwnerMatch(result, ownerUserId)) return resolve(null);
          return resolve(result);
        };
      });
    } catch (error) {
      console.error('Error getting inspection draft from cache:', error);
      return null;
    }
  }

  async getAllDrafts() {
    try {
      await this.dbPromise;
      const ownerUserId = getCurrentOwnerUserId();
      return new Promise((resolve, reject) => {
        const tx = this.db.transaction([STORE_NAME], 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const result = request.result || [];
          const filtered = ownerUserId
            ? result.filter((d) => isOwnerMatch(d, ownerUserId))
            : result;

          // Safety net: collapse any duplicates by responseId, keeping the most recently updated entry.
          const byResponse = new Map();
          const passthrough = [];
          for (const d of (filtered || [])) {
            const resp = this.normalizeResponseId(d?.responseId || d?.data?.responseId);
            if (!resp) {
              passthrough.push(d);
              continue;
            }
            const existing = byResponse.get(resp);
            if (!existing) {
              byResponse.set(resp, d);
              continue;
            }
            const existingUpdated = Number(existing?.updatedOn || 0);
            const nextUpdated = Number(d?.updatedOn || 0);
            if (nextUpdated >= existingUpdated) {
              byResponse.set(resp, d);
            }
          }

          resolve([...byResponse.values(), ...passthrough]);
        };
      });
    } catch (error) {
      console.error('Error getting all inspection drafts from cache:', error);
      return [];
    }
  }

  async deleteDraft(cacheKey) {
    try {
      await this.dbPromise;
      return new Promise((resolve, reject) => {
        const tx = this.db.transaction([STORE_NAME], 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.delete(cacheKey);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(true);
      });
    } catch (error) {
      console.error('Error deleting inspection draft from cache:', error);
      return false;
    }
  }

  async clearAllDrafts() {
    try {
      await this.dbPromise;
      return new Promise((resolve, reject) => {
        const tx = this.db.transaction([STORE_NAME], 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.clear();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(true);
      });
    } catch (error) {
      console.error('Error clearing inspection draft cache:', error);
      return false;
    }
  }
}

const inspectionCacheManager = new InspectionCacheManager();

export default inspectionCacheManager;
// Generate a brand-new draft ID (also used as cache key by default)
export const generateNewInspectionDraftId = () =>
  inspectionCacheManager.generateDraftId();

export const saveInspectionDraft = (draft) =>
  inspectionCacheManager.saveDraft(draft);

export const getInspectionDraft = (cacheKey) =>
  inspectionCacheManager.getDraft(cacheKey);

export const getAllInspectionDrafts = () =>
  inspectionCacheManager.getAllDrafts();

export const deleteInspectionDraft = (cacheKey) =>
  inspectionCacheManager.deleteDraft(cacheKey);

export const clearAllInspectionDrafts = () =>
  inspectionCacheManager.clearAllDrafts();
